import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useStockLots } from '@/features/stock-lots/queries';
import type { StockLot } from '@/features/stock-lots/types';
import { cn } from '@/lib/utils';

/**
 * Discriminated union over the four backend movement actions. The
 * shape feeds directly into the matching mutation hook in F7.4
 * (`useReceipt/Consumption/Adjustment/TransferMovement`).
 *
 *   receipt      qty positive         + reason optional
 *   consumption  qty positive         + reason optional + WO link optional
 *   adjustment   delta signed non-0   + reason MANDATORY
 *   transfer     destLotId + qty pos  + reason optional
 *                (sourceLotId is the current lot, fixed by caller)
 */
export type StockMovementKind =
  | 'receipt'
  | 'consumption'
  | 'adjustment'
  | 'transfer';

export type StockMovementSubmitPayload =
  | { kind: 'receipt'; quantity: number; reason: string | null }
  | {
      kind: 'consumption';
      quantity: number;
      reason: string | null;
      relatedWorkOrderId: string | null;
    }
  | { kind: 'adjustment'; delta: number; reason: string }
  | {
      kind: 'transfer';
      destLotId: string;
      quantity: number;
      reason: string | null;
    };

interface StockMovementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Which kind of movement to record. */
  kind: StockMovementKind;
  /** The current lot (source for transfers, target for the others). */
  currentLot: StockLot;
  /** Free-form effective unit for display ("L", "piece", etc.). */
  effectiveUnit: string | null;
  /** Pre-translated error message (caller resolves ApiError). */
  errorMessage: string | null;
  /** Mutation pending flag. */
  loading: boolean;
  /**
   * Caller invokes the matching mutation hook. The modal closes
   * itself only after onConfirm has been triggered AND `loading`
   * goes back to false (caller must invoke onOpenChange(false) on
   * success).
   */
  onConfirm: (payload: StockMovementSubmitPayload) => void;
}

/**
 * StockMovementModal — single modal that morphs based on `kind`.
 *
 * The modal keeps its OWN local state (cleared on every open) so
 * cancel + reopen always lands on a clean form. The submit button
 * label changes per kind. canSubmit() prevents invalid payloads
 * from being submitted (qty>=1, delta!=0, transfer requires
 * destLot).
 *
 * For transfer, an in-modal PartLotPicker shows other lots of the
 * SAME part on ANY vessel (cross-vessel transfers are allowed by
 * the backend as long as the part matches). The current lot is
 * excluded.
 */
export function StockMovementModal({
  open,
  onOpenChange,
  kind,
  currentLot,
  effectiveUnit,
  errorMessage,
  loading,
  onConfirm,
}: StockMovementModalProps) {
  const { t } = useTranslation();

  // Local state cleared on every open transition so reopening is
  // always pristine, regardless of the previous outcome.
  const [quantity, setQuantity] = useState<string>('');
  const [delta, setDelta] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [relatedWorkOrderId, setRelatedWorkOrderId] = useState<string>('');
  const [destLotId, setDestLotId] = useState<string>('');

  useEffect(() => {
    if (open) {
      setQuantity('');
      setDelta('');
      setReason('');
      setRelatedWorkOrderId('');
      setDestLotId('');
    }
  }, [open, kind]);

  // Filter candidate lots for transfers — same part, different lot,
  // not soft-deleted. We fetch all lots without filters and cull
  // client-side; for typical fleets the lots count stays manageable.
  const { data: allLots } = useStockLots();
  const transferCandidates = (allLots ?? []).filter(
    (l) => l.partId === currentLot.partId && l.id !== currentLot.id,
  );

  const qtyNum = quantity === '' ? NaN : Number(quantity);
  const deltaNum = delta === '' ? NaN : Number(delta);

  const canSubmit = (() => {
    switch (kind) {
      case 'receipt':
      case 'consumption':
        return Number.isFinite(qtyNum) && qtyNum >= 1;
      case 'adjustment':
        return Number.isFinite(deltaNum) && deltaNum !== 0 && reason.trim().length > 0;
      case 'transfer':
        return Boolean(destLotId) && Number.isFinite(qtyNum) && qtyNum >= 1;
    }
  })();

  const submit = () => {
    if (!canSubmit) return;
    switch (kind) {
      case 'receipt':
        onConfirm({
          kind: 'receipt',
          quantity: Math.trunc(qtyNum),
          reason: reason.trim() || null,
        });
        break;
      case 'consumption':
        onConfirm({
          kind: 'consumption',
          quantity: Math.trunc(qtyNum),
          reason: reason.trim() || null,
          relatedWorkOrderId: relatedWorkOrderId.trim() || null,
        });
        break;
      case 'adjustment':
        onConfirm({
          kind: 'adjustment',
          delta: Math.trunc(deltaNum),
          reason: reason.trim(),
        });
        break;
      case 'transfer':
        onConfirm({
          kind: 'transfer',
          destLotId,
          quantity: Math.trunc(qtyNum),
          reason: reason.trim() || null,
        });
        break;
    }
  };

  const titleKey = `stockMovement.modal.${kind}.title`;
  const descKey = `stockMovement.modal.${kind}.description`;
  const submitKey = `stockMovement.modal.${kind}.submit`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>{t(descKey)}</DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>{t('stockMovement.modal.saveError')}</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          {/* Quantity (receipt + consumption + transfer) */}
          {(kind === 'receipt' ||
            kind === 'consumption' ||
            kind === 'transfer') && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="mvt-qty">
                {t('stockMovement.modal.quantityLabel')}
                {effectiveUnit && (
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    ({effectiveUnit})
                  </span>
                )}
              </label>
              <Input
                id="mvt-qty"
                type="number"
                min={1}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
              />
              <p className="text-xs text-muted-foreground">
                {t('stockMovement.modal.quantityHint')}
              </p>
            </div>
          )}

          {/* Delta (adjustment only — signed) */}
          {kind === 'adjustment' && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="mvt-delta">
                {t('stockMovement.modal.deltaLabel')}
                {effectiveUnit && (
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    ({effectiveUnit})
                  </span>
                )}
              </label>
              <Input
                id="mvt-delta"
                type="number"
                step={1}
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                placeholder="-3"
              />
              <p className="text-xs text-muted-foreground">
                {t('stockMovement.modal.deltaHint')}
              </p>
            </div>
          )}

          {/* Destination lot (transfer only) */}
          {kind === 'transfer' && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="mvt-dest">
                {t('stockMovement.modal.destLabel')}
              </label>
              {transferCandidates.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    {t('stockMovement.modal.noDestAvailable')}
                  </AlertDescription>
                </Alert>
              ) : (
                <select
                  id="mvt-dest"
                  value={destLotId}
                  onChange={(e) => setDestLotId(e.target.value)}
                  className={cn(
                    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  )}
                >
                  <option value="">
                    {t('stockMovement.modal.destPlaceholder')}
                  </option>
                  {transferCandidates.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.location} ({t('stockLot.fields.quantityOnHand')}:{' '}
                      {l.quantityOnHand})
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-muted-foreground">
                {t('stockMovement.modal.destHint')}
              </p>
            </div>
          )}

          {/* Optional WO link (consumption only) */}
          {kind === 'consumption' && (
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="mvt-wo">
                {t('stockMovement.modal.relatedWorkOrderLabel')}
              </label>
              <Input
                id="mvt-wo"
                value={relatedWorkOrderId}
                onChange={(e) => setRelatedWorkOrderId(e.target.value)}
                placeholder={t('stockMovement.modal.relatedWorkOrderPlaceholder')}
              />
              <p className="text-xs text-muted-foreground">
                {t('stockMovement.modal.relatedWorkOrderHint')}
              </p>
            </div>
          )}

          {/* Reason — optional except for adjustment */}
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="mvt-reason">
              {t(
                kind === 'adjustment'
                  ? 'stockMovement.modal.reasonRequiredLabel'
                  : 'stockMovement.modal.reasonLabel',
              )}
            </label>
            <Input
              id="mvt-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t(`stockMovement.modal.${kind}.reasonPlaceholder`)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={submit}
            disabled={!canSubmit || loading}
          >
            {loading && <Loader2 className="animate-spin" aria-hidden />}
            {t(submitKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Lightweight lot-picker skeleton placeholder used while the lot
 * list is loading — kept minimal since the modal is only ever
 * opened from a detail page where the data should already be in
 * cache anyway.
 */
export function StockMovementModalSkeleton() {
  return <Skeleton className="h-64 w-full" />;
}
